import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Nav, Form, Row, Col, Table } from 'react-bootstrap';
import { Dropdown } from 'primereact/dropdown';

// Internal Components
import Header from '../Include/Header';
import Sidebar from '../Include/Sidebar';
import Footer from '../Include/Footer';
import StepIndicator from '../Components/StoreQuotation/StepIndicator';
import ComparativeTable from '../Components/StoreQuotation/ComparativeTable';
import ItemList from '../Components/StoreQuotation/ItemList';
import PartyList from '../Components/StoreQuotation/PartyList';

// Modals
import CreateQuotationModal from '../Components/StoreQuotation/modals/CreateQuotationModal';
import SendPartiesModal from '../Components/StoreQuotation/modals/SendPartiesModal';
import AddRatesModal from '../Components/StoreQuotation/modals/AddRatesModal';
import FinalizeModal from '../Components/StoreQuotation/modals/FinalizeModal';

// Constants & Helpers
import {
  STATUS_CONFIG,
  fmt,
} from '../../../helper/storeQuotation';

import { useDispatch, useSelector } from 'react-redux';
import { createStoreQuotation } from '../../../Store/Store/StoreQuotation/createStoreQuotation';
import { getAdminItem } from '../../../Store/Store/Item/AdminItem';
import { updateStoreQuotation } from '../../../Store/Store/StoreQuotation/updateStoreQuotation';
import { addPartyRates } from '../../../Store/Store/StoreQuotation/partyRates';
import { getAdminParty } from '../../../Store/Store/Party/AdminParty';
import { sendQuotationParty } from '../../../Store/Store/StoreQuotation/sendQuotationParty';
import { getOneStoreQuotation } from '../../../Store/Store/StoreQuotation/getOneStoreQuotation';
import moment from 'moment';
import ReactQuill from 'react-quill';
import { quillFormats, quillModules } from '../../../helper/quotation';
import { Edit } from 'lucide-react';

const DEFAULT_PO_TERMS = `1. Materials should be Supplied for the Specifications and Quality standards.<br />2. Delivery Within The Distribution Time is Mandatory.<br />3. Any Defect of Materials Will be returned.<br />4. Supplier Must Submit a Proper Tax Invoice With GST Details.<br />5. Po Number Must be Mentioned on All Invoices and Delivery Challan. <br />
<br />Thanks for doing business with us.<br />
`;

const ManageStoreQuotation = () => {
  const [quotations, setQuotations] = useState([]);
  const [view, setView] = useState("add"); // "add", "list" or "detail"
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState("comparative");
  const [modal, setModal] = useState(null);
  const [ratesPartyId, setRatesPartyId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    voucher_date: "",
    estimate_details: {
      // vendor_code: "",
      supply_place: "",
      reference_no: "",
      enquiry_no: ""
    },
    items: [],
    terms_and_condition: "",
    internal_note: ""
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    let newErrors = {};
    if (!form.voucher_date) newErrors.voucher_date = "Voucher Date is required";
    // if (!form.estimate_details.vendor_code) newErrors.vendor_code = "Vendor Code is required";

    if (form.items.length === 0) {
      newErrors.items = "Please add at least one item";
    } else {
      form.items.forEach((item, idx) => {
        if (!item.item_id) newErrors[`item_${idx}_item_id`] = "Item selection is required";
        if (!item.quantity) newErrors[`item_${idx}_quantity`] = "Quantity is required";
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const setFormField = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const setDetailField = (field, val) => {
    setForm(f => ({ ...f, estimate_details: { ...f.estimate_details, [field]: val } }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // item details
  const addItem = () => {
    setFormField("items", [...form.items, { item_id: "", item_name: "", description: "", size: "", unit_name: "", hsn_no: "", quantity: "" }]);
    if (errors.items) setErrors(prev => ({ ...prev, items: null }));
  };
  const removeItem = (idx) => {
    setFormField("items", form.items.filter((_, i) => i !== idx));
  };
  const updateItem = (idx, field, val) => {
    setFormField("items", form.items.map((item, i) => i === idx ? { ...item, [field]: val } : item));
    if (errors[`item_${idx}_${field}`]) setErrors(prev => ({ ...prev, [`item_${idx}_${field}`]: null }));
  };

  const handleSubmit = (e) => {
    if (!validate()) return;
    handleCreate(form);
  };

  const resetForm = () => {
    setForm({
      voucher_date: "",
      estimate_details: {
        // vendor_code: "",
        supply_place: "", reference_no: "", enquiry_no: ""
      },
      items: [],
      terms_and_condition: "",
    });
    setErrors({});
  };

  useEffect(() => {
    if (location.state) {
      setSelected(location.state);
      setView("detail");
      if (location.state._id) {
        dispatch(getOneStoreQuotation(location.state._id));
      }
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    if (selected?._id && view === "detail") {
      dispatch(getOneStoreQuotation(selected._id));
    }
  }, [selected?._id, view, dispatch]);

  const handleOpenSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // --- Detail View Helpers ---
  const currentStep = useMemo(() => {
    if (!selected) return 0;
    const map = {
      draft: 0,
      sent: 1,
      partially_received: 2,
      received: 2,
      under_review: 2,
      finalized: 3,
      ordered: 3,
      cancelled: 3
    };
    return map[selected.status] ?? 0;
  }, [selected]);

  useEffect(() => {
    dispatch(getAdminItem({ is_main: true }))
    dispatch(getAdminParty({ storeType: "1", is_main: true }));
  }, [])

  const fetchComparative = () => {
    dispatch(getOneStoreQuotation(selected._id));
  }

  useEffect(() => {
    if (activeTab === "comparative" && selected?._id) {
      fetchComparative();
    }
  }, [activeTab, selected?._id, dispatch])

  const canSendParties = selected && !selected.is_finalized;
  const canAddRates = selected && ["sent", "partially_received", "received", "under_review"].includes(selected.status);
  const canFinalize = selected && !selected.is_finalized && selected.parties?.some(p => p.party_status === "rate_received");
  const canEdit = selected && !selected.is_finalized && !["finalized", "ordered", "cancelled"].includes(selected.status);

  const itemData = useSelector((state) => state?.getAdminItem?.user?.data)
  const partyDataList = useSelector((state) => state?.getAdminParty?.user?.data)
  const fetchedQuotation = useSelector((state) => state?.getOneStoreQuotation?.user?.data)

  useEffect(() => {
    if (fetchedQuotation && selected?._id === fetchedQuotation._id) {
      setSelected(fetchedQuotation);
    }
  }, [fetchedQuotation])

  // --- Actions ---
  const handleCreate = async (formData) => {
    const { items, ...rest } = formData;
    const payload = {
      firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
      items: items.map(item => ({
        item_id: item.item_id,
        item_name: item.item_name,
        quantity: item.quantity,
        description: item.description,
        size: item.size,
        unit_name: item.unit_name,
        hsn_no: item.hsn_no,
      })),
      ...rest
    }

    const result = await dispatch(createStoreQuotation(payload));

    if (result?.payload?.data) {
      const newQ = {
        ...result.payload.data,
        status: result.payload.data.status || "draft",
        is_finalized: result.payload.data.is_finalized || false,
        parties: result.payload.data.parties || [],
        total_quantity: result.payload.data.total_quantity || formData.items?.reduce((acc, i) => acc + (parseFloat(i.quantity) || 0), 0) || 0,
        total_amount: result.payload.data.total_amount || 0,
        total_gst: result.payload.data.total_gst || 0,
        total_net_amount: result.payload.data.total_net_amount || 0,
        createdAt: result.payload.data.createdAt || new Date().toISOString()
      };
      setQuotations(prev => [newQ, ...prev]);
      setSelected(newQ);
      setView("detail");
      setModal(null);
      resetForm();
      navigate(location.pathname + location.search, { state: newQ, replace: true });
    }
  };

  const handleUpdate = (updated) => {
    dispatch(updateStoreQuotation({ id: updated._id, payload: updated }));
    setQuotations(prev => prev.map(q => q._id === updated._id ? updated : q));
    setSelected(updated);
    navigate(location.pathname + location.search, { state: updated, replace: true });
  };

  // parties
  const handleSendParties = async (partyIds) => {
    if (!selected) return;

    const payload = {
      party_ids: partyIds
    };

    try {
      const result = await dispatch(sendQuotationParty({ id: selected._id, payload }));
      if (result?.payload?.success) {
        setModal(null);
        fetchComparative();
      }
    } catch (error) {
      console.error("Error sending quotation:", error);
    }
  };

  // party rates
  const handleSaveRates = async (partyId, rates, terms) => {
    if (!selected) return;

    const item_rates = rates
      .filter(r => r.rate)
      .map(r => ({
        item_main_id: r.item_main_id,
        rate: parseFloat(r.rate),
        discount: parseFloat(r.discount) || 0,
        gst_percent: parseFloat(r.gst_percent) || 18,
        make: r.make || null,
        remark: r.remark || null
      }));

    const payload = {
      party_id: partyId,
      item_rates,
      delivery_terms: terms.delivery_terms,
      payment_terms: terms.payment_terms,
      delivery_date: terms.delivery_date,
      loading_charge: terms.loading_charge,
      remark: terms.remark || null,
    };

    try {
      const result = await dispatch(addPartyRates({ id: selected._id, payload }));
      if (result?.payload?.success) {
        fetchComparative();
        setModal(null);
      }
    } catch (error) {
      console.error("Error saving party rates:", error);
    }
  };

  return (
    <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
      <Header handleOpen={handleOpenSidebar} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/main-store/user/dashboard">Dashboard </Link></li>
                  <li className="breadcrumb-item"><i className="feather-chevron-right"></i></li>
                  <li className="breadcrumb-item"><Link to="/main-store/user/store-quotation">Store Quotation </Link></li>
                  <li className="breadcrumb-item"><i className="feather-chevron-right"></i></li>
                  <li className="breadcrumb-item active">Store Quotation Management</li>
                </ul>
              </div>
            </div>
          </div>

          {view === "add" ? (
            <>
              <Card className="shadow-sm border-0 rounded-3 bg-white">
                <Card.Body className="p-4">
                  <Form>
                    <Row className="mb-3">
                      <Col md={3} className='mb-3'>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Voucher Date <span className='text-danger'>*</span></Form.Label>
                          <Form.Control size="sm" type="date" value={form.voucher_date} onChange={e => setFormField("voucher_date", e.target.value)} isInvalid={!!errors.voucher_date} />
                          {errors.voucher_date && <Form.Control.Feedback type="invalid">{errors.voucher_date}</Form.Control.Feedback>}
                        </Form.Group>
                      </Col>
                      {/* <Col md={3} className='mb-3'>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Vendor Code <span className='text-danger'>*</span></Form.Label>
                          <Form.Control size="sm" type="text" value={form.estimate_details.vendor_code} onChange={e => setDetailField("vendor_code", e.target.value)} placeholder="Enter Vendor Code" isInvalid={!!errors.vendor_code} />
                          {errors.vendor_code && <Form.Control.Feedback type="invalid">{errors.vendor_code}</Form.Control.Feedback>}
                        </Form.Group>
                      </Col> */}
                      <Col md={3} className='mb-3'>
                        <Form.Group>
                          <Form.Label className="small fw-bold ">Supply Place</Form.Label>
                          <Form.Control size="sm" value={form.estimate_details.supply_place} onChange={e => setDetailField("supply_place", e.target.value)} placeholder='Enter Supply Place' />
                        </Form.Group>
                      </Col>
                      <Col md={3} className='mb-3'>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Reference No</Form.Label>
                          <Form.Control size="sm" value={form.estimate_details.reference_no} onChange={e => setDetailField("reference_no", e.target.value)} placeholder='Enter Reference No' />
                        </Form.Group>
                      </Col>
                      <Col md={3} className='mb-3'>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Enquiry No</Form.Label>
                          <Form.Control size="sm" value={form.estimate_details?.enquiry_no} onChange={e => setDetailField("enquiry_no", e.target.value)} placeholder='Enter Enquiry No' />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className='mb-3'>
                      <Col md={12} className="mb-3">
                        <Form.Group>
                          <Form.Label className="small fw-bold">Internal Note</Form.Label>
                          <Form.Control as="textarea" rows={3} value={form.internal_note} onChange={e => setFormField("internal_note", e.target.value)} />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h4 className="mb-0 fw-bold" style={{ color: '#444648ff' }}>Items / Requirements</h4>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={addItem}
                        className="fw-bold px-3"
                        disabled={form.items.some(it => !it.item_id || !it.quantity)}
                      >
                        + Add Item
                      </Button>
                    </div>

                    {form.items.length === 0 && (
                      <div className="text-center py-4 text-muted rounded mb-4" style={{ border: '1px dashed #ced4da', backgroundColor: '#f8f9fa', fontSize: '13px' }}>
                        No items added yet. Click "Add Item" to start.
                      </div>
                    )}

                    {errors.items && <div className="text-danger small mb-3">{errors.items}</div>}

                    {form.items.map((item, idx) => (
                      <div key={idx} className="p-3 mb-5 rounded border shadow-sm" style={{ backgroundColor: '#ffffff' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3 pb-2">
                          <span className="fw-bold" style={{ fontSize: '13px', color: '#f97316' }}>Item #{idx + 1}</span>
                          <Button variant="danger" size="sm" onClick={() => removeItem(idx)} className="px-3 py-1" style={{ fontSize: '12px', borderRadius: '4px' }}>Remove</Button>
                        </div>
                        <Row className="g-3">
                          <Col md={4} sm={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold text-muted mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Item Name <span className="text-danger">*</span></Form.Label>
                              <Dropdown
                                value={item.item_id}
                                onChange={(e) => {
                                  const selectedItem = itemData?.find(it => it._id === e.value);

                                  setFormField(
                                    "items",
                                    form.items.map((it, i) =>
                                      i === idx
                                        ? {
                                          ...it,
                                          item_id: selectedItem?._id || "",
                                          item_name: selectedItem?.name || "",
                                          hsn_no: selectedItem?.hsn_code || "",
                                          unit_name: selectedItem?.unit?.name || "PCS"
                                        }
                                        : it
                                    )
                                  );
                                }}
                                options={itemData?.map(opt => ({
                                  label: opt.name,
                                  value: opt._id
                                })) || []}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select Item"
                                filter
                                className={`w-100 ${errors[`item_${idx}_item_id`] ? 'p-invalid' : ''}`}
                              />
                              {errors[`item_${idx}_item_id`] && <div className="text-danger small mt-1" style={{ fontSize: '11px' }}>{errors[`item_${idx}_item_id`]}</div>}
                            </Form.Group>
                          </Col>
                          <Col md={4} sm={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold text-muted mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Size</Form.Label>
                              <Form.Control size="sm" type="text" value={item.size} onChange={e => updateItem(idx, "size", e.target.value)} placeholder="e.g. 16mm, AG-4" className="py-2" />
                            </Form.Group>
                          </Col>
                          <Col md={4} sm={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold text-muted mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>HSN Code</Form.Label>
                              <Form.Control size="sm" type='number' value={item.hsn_no} onChange={e => updateItem(idx, "hsn_no", e.target.value)} placeholder="84641000" className="py-2" />
                            </Form.Group>
                          </Col>
                          <Col md={4} sm={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold text-muted mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Unit</Form.Label>
                              <Form.Control
                                size="sm"
                                value={item.unit_name}
                                readOnly
                                placeholder="Unit"
                                className="py-2"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4} sm={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold text-muted mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Quantity <span className='text-danger'>*</span></Form.Label>
                              <Form.Control size="sm" type="number" value={item.quantity} onChange={e => updateItem(idx, "quantity", e.target.value)} placeholder="0" className="py-2" isInvalid={!!errors[`item_${idx}_quantity`]} />
                              {errors[`item_${idx}_quantity`] && <Form.Control.Feedback type="invalid">{errors[`item_${idx}_quantity`]}</Form.Control.Feedback>}
                            </Form.Group>
                          </Col>
                          <Col md={4} sm={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold text-muted mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Description</Form.Label>
                              <Form.Control size="sm" type="text" value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Brief description" className="py-2" />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    ))}

                    {/* terms and condition section  */}
                    <div className="row">
                      <div className="col-12">
                        <div
                          className="card shadow-sm h-100"
                          style={{ border: "1px solid #e0e0e0" }}
                        >
                          <div
                            className="card-header"
                            style={{
                              backgroundColor: "white",
                              border: "1px solid var(--primary-clr)",
                              color: "var(--primary-clr)",
                            }}
                          >
                            <h6
                              className="mb-0 fw-semibold"
                              style={{ color: "var(--primary-clr)", fontSize: "15px" }}
                            >
                              <i
                                className="fa fa-file-text me-2"
                                style={{ color: "var(--primary-clr)" }}
                              ></i>
                              Terms and Conditions
                            </h6>
                          </div>

                          <div
                            className="card-body p-0 terms-editor-wrapper"
                            style={{ height: "300px", overflow: "hidden" }}
                          >
                            <ReactQuill
                              theme="snow"
                              value={form.terms_and_condition}
                              onChange={e => setFormField("terms_and_condition", e)}
                              modules={quillModules}
                              formats={quillFormats}
                              style={{ height: "280px" }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        variant="primary"
                        size="md"
                        className='rounded-2 px-4 shadow-sm'
                        onClick={handleSubmit}
                      >
                        Create Quotation
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>

            </>
          ) : (
            <div>
              {/* Detail Header */}
              {selected && (
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div>
                      <h3 className="mb-0 text-orange d-inline-block me-2">{selected.voucher_no}</h3>
                      <span className="badge me-2" style={{
                        backgroundColor: STATUS_CONFIG[selected.status]?.bg || '#6c757d',
                        color: STATUS_CONFIG[selected.status]?.color || '#fff'
                      }}>
                        {STATUS_CONFIG[selected.status]?.label || selected.status}
                      </span>
                      {selected.is_finalized && <span className="badge bg-success">Finalized</span>}
                      <div className="text-muted small mt-1">
                        {localStorage?.getItem("PAY_USER_FIRM_NAME")} • {moment(selected.voucher_date).format('DD MMM YYYY')}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    {canEdit &&
                      <>
                        <Button variant="outline-primary" size="sm" className='px-3 rounded-2 d-flex align-items-center gap-2' onClick={() => setModal("edit")}> <Edit size={14} /> Edit Quotation</Button>
                      </>
                    }

                    {canSendParties && (
                      <Button variant="primary" size="sm" className='px-3 rounded-2' onClick={() => setModal("send")}>
                        {selected.parties?.length ? "+ Add Parties" : "Send to Parties"}
                      </Button>
                    )}
                    {canFinalize &&
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => setModal("finalize")}
                        className='px-4 rounded-2'
                      >
                        <i className="fa fa-check-circle me-1"></i>Finalize
                      </Button>}
                    {selected.is_finalized && (
                      <>
                        <div className="d-flex align-items-center gap-3 bg-white p-2 px-3 rounded-3 border">
                          <div className="d-flex flex-column">
                            <span className="text-muted fw-bold text-center mb-0" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Net</span>
                            <span className="text-secondary" style={{ fontSize: '11px' }}>( Item + Loading + GST )</span>
                          </div>
                          <div className="vr mx-1" style={{ height: '30px' }}></div>
                          <div className="text-success fw-bold h5 mb-0" style={{ fontSize: '1.1rem' }}>
                            ₹{fmt(selected.total_payable)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <StepIndicator current={currentStep} />

              <Card className=" border-0 mt-4 mb-4 rounded-3 bg-white">
                <div className="">
                  <Nav
                    variant="tabs"
                    style={{ borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px" }}
                    className="modern-tabs border-0 flex-nowrap"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                  >
                    <Nav.Item>
                      <Nav.Link
                        eventKey="comparative"
                        className="d-flex align-items-center text-decoration-none"
                      >
                        <i className={`feather-bar-chart-2 me-2 ${activeTab === 'comparative' ? '' : 'text-muted'}`}></i>
                        Comparative Table
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="items"
                        className="d-flex align-items-center text-decoration-none"
                      >
                        <i className={`feather-box me-2 ${activeTab === 'items' ? '' : 'text-muted'}`}></i>
                        Items
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="parties"
                        className="d-flex align-items-center text-decoration-none"
                      >
                        <i className={`feather-users me-2 ${activeTab === 'parties' ? '' : 'text-muted'}`}></i>
                        Parties
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
              </Card>

              <Card className="shadow-sm border-0 rounded-3 bg-white">
                <Card.Body className="p-0">
                  <div className="tab-anim-fade" key={activeTab}>
                    {selected && activeTab === "comparative" && <ComparativeTable quotation={fetchedQuotation} />}
                    {selected && activeTab === "items" && <ItemList quotation={selected} />}
                    {selected && activeTab === "parties" && <PartyList quotation={selected} onAddRates={(pid) => { setRatesPartyId(pid); setModal("rates"); }} onViewRates={(pid) => { setRatesPartyId(pid); setModal("view_rates"); }} onRefresh={fetchComparative} />}
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
        </div>
        <Footer />
      </div>

      {/* Modals */}
      {modal === "create" && <CreateQuotationModal onSave={handleCreate} onClose={() => setModal(null)} />}
      {modal === "edit" && selected && <CreateQuotationModal id={selected._id} initial={selected} onSave={(f) => { handleUpdate({ ...selected, ...f }); setModal(null); }} onClose={() => setModal(null)} />}
      {modal === "send" && selected && <SendPartiesModal quotation={selected} onSave={handleSendParties} onClose={() => setModal(null)} />}
      {modal === "rates" && selected && <AddRatesModal quotation={selected} partyId={ratesPartyId} onSave={handleSaveRates} onClose={() => setModal(null)} />}
      {modal === "view_rates" && selected && <AddRatesModal quotation={selected} partyId={ratesPartyId} viewOnly={true} onClose={() => setModal(null)} />}
      {modal === "finalize" && selected && <FinalizeModal quotation={selected} onRefresh={fetchComparative} onClose={() => setModal(null)} />}
    </div >
  );
};

export default ManageStoreQuotation;